import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Check, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Entity type options with pricing
const ENTITY_TYPES = [
  {
    id: 'llc_formation',
    type: 'us_llc',
    name: 'LLC',
    description: 'Most popular for small businesses. Flexible management, pass-through taxation.',
    baseFee: 199
  },
  {
    id: 'c_corp_formation',
    type: 'us_c_corp',
    name: 'C-Corp',
    description: 'Best for startups seeking investment. Can issue stock to investors.',
    baseFee: 299
  },
  {
    id: 's_corp_formation',
    type: 'us_s_corp',
    name: 'S-Corp',
    description: 'Tax benefits for eligible small businesses. Pass-through taxation.',
    baseFee: 149
  }
];

// Fixed service fees
const SERVICE_FEES = {
  ein: { name: 'EIN (Tax ID)', fee: 99, required: true },
  registered_agent: { name: 'Registered Agent', fee: 150, recurring: '/yr', required: true },
  mail_forwarding: { name: 'Mail Forwarding', fee: 200, recurring: '/yr', required: false }
};

// Bank details for bank transfer
const BANK_DETAILS = {
  bank_name: 'Wells Fargo',
  account_name: 'StartEase Agent LLC',
  account_number: 'XXXX-XXXX-1234',
  routing_number: 'XXX-XXX-XXX',
  swift_code: 'WFBIUS6S',
};

// Document requirements per service type (hardcoded - no DB fetch needed)
const SERVICE_DOCUMENT_REQUIREMENTS = {
  llc_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ],
  c_corp_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ],
  s_corp_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ]
};

const FormationPackagePage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Ref to prevent duplicate order creation (handles race conditions)
  const isCreatingOrdersRef = useRef(false);
  const createdOrderIdsRef = useRef(new Set());
  
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false); // Prevent double submission
  const [creatingOrders, setCreatingOrders] = useState(false); // Show loading state
  const [error, setError] = useState('');
  
  // Selections
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [includeMailForwarding, setIncludeMailForwarding] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  
  // Pricing
  const [stateFee, setStateFee] = useState(0);
  const [products, setProducts] = useState({});
  
  // Success state
  const [orderSuccess, setOrderSuccess] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${API}/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompany(res.data);
      } catch (err) {
        console.error('Error fetching company:', err);
        setError('Failed to load company details');
      } finally {
        setLoading(false);
      }
    };
    
    if (token && companyId) fetchCompany();
  }, [token, companyId]);

  // Fetch state fee - returns the fee value directly for use in order creation
  const fetchStateFeeForService = async (serviceType, state) => {
    try {
      const response = await axios.get(`${API}/state-fees/lookup`, {
        params: {
          service_type: serviceType,
          state: state
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fees = response.data.state_fees || [];
      if (fees.length > 0) {
        return fees[0].govt_fee || 0;
      }
      return 0;
    } catch (err) {
      console.error('Error fetching state fee for', serviceType, ':', err);
      return 0;
    }
  };

  // Fetch state fee when entity is selected (for display purposes)
  useEffect(() => {
    const fetchStateFee = async () => {
      if (!selectedEntity || !company) return;
      
      const fee = await fetchStateFeeForService(selectedEntity.id, company.state_of_formation);
      console.log('State fee fetched for display:', fee);
      setStateFee(fee);
    };
    
    fetchStateFee();
  }, [selectedEntity, company, token]);

  // Calculate total
  const calculateTotal = () => {
    if (!selectedEntity) return 0;
    
    let total = selectedEntity.baseFee + stateFee;
    total += SERVICE_FEES.ein.fee;
    total += SERVICE_FEES.registered_agent.fee;
    if (includeMailForwarding) {
      total += SERVICE_FEES.mail_forwarding.fee;
    }
    
    return total;
  };

  const handleNext = async () => {
    if (step === 1 && !selectedEntity) {
      setError('Please select an entity type');
      return;
    }
    
    // When proceeding from Step 1, update the company's entity type if different
    if (step === 1 && selectedEntity && company && company.entity_type !== selectedEntity.type) {
      try {
        await axios.put(`${API}/companies/${companyId}`, {
          entity_type: selectedEntity.type
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local company state
        setCompany(prev => ({ ...prev, entity_type: selectedEntity.type }));
      } catch (err) {
        console.error('Error updating company entity type:', err);
        // Continue anyway - the backend will validate during order creation
      }
    }
    
    setError('');
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  // Create orders for all services using service_type (no product_id dependency)
  const createOrders = async () => {
    // Prevent duplicate order creation using ref (handles race conditions)
    if (isCreatingOrdersRef.current) {
      console.log('Order creation already in progress, skipping...');
      return [];
    }
    isCreatingOrdersRef.current = true;
    setCreatingOrders(true);
    
    try {
      const orders = [];
      
      // Fetch state fee fresh at order creation time to avoid stale state
      const formationStateFee = await fetchStateFeeForService(selectedEntity.id, company.state_of_formation);
      console.log('Formation state fee at order creation:', formationStateFee);
      
      // Define all order types to create
      const orderConfigs = [
        {
          service_type: selectedEntity.id,
          total_amount: selectedEntity.baseFee + formationStateFee,
          govt_fee: formationStateFee,
          platform_fee: selectedEntity.baseFee
        },
        {
          service_type: 'ein_application',
          total_amount: SERVICE_FEES.ein.fee,
          govt_fee: 0,
          platform_fee: SERVICE_FEES.ein.fee
        },
        {
          service_type: 'registered_agent',
          total_amount: SERVICE_FEES.registered_agent.fee,
          govt_fee: 0,
          platform_fee: SERVICE_FEES.registered_agent.fee
        }
      ];
      
      // Add mail forwarding if selected
      if (includeMailForwarding) {
        orderConfigs.push({
          service_type: 'mail_forwarding',
          total_amount: SERVICE_FEES.mail_forwarding.fee,
          govt_fee: 0,
          platform_fee: SERVICE_FEES.mail_forwarding.fee
        });
      }
      
      // Create all orders - skip if already created for this service type
      const createdOrders = [];
      for (const config of orderConfigs) {
        // Skip if we already created this order type in this session
        const orderKey = `${companyId}-${config.service_type}`;
        if (createdOrderIdsRef.current.has(orderKey)) {
          console.log(`Skipping duplicate order for ${config.service_type}`);
          continue;
        }
        
        try {
          const orderData = {
            company_id: companyId,
            service_type: config.service_type,
            total_amount: config.total_amount,
            govt_fee: config.govt_fee,
            platform_fee: config.platform_fee,
            collected_information: {}
          };
          
          console.log('Creating order:', orderData);
          const res = await axios.post(`${API}/orders`, orderData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Order created successfully:', res.data);
          
          // Mark as created
          createdOrderIdsRef.current.add(orderKey);
          createdOrders.push(res.data);
        } catch (err) {
          // If order already exists (idempotency), log but don't fail
          if (err.response?.status === 409 || err.response?.data?.detail?.includes('already exists')) {
            console.log(`Order for ${config.service_type} already exists, skipping...`);
            createdOrderIdsRef.current.add(orderKey);
            continue;
          }
          console.error('Error creating order:', err);
          console.error('Error response:', err.response?.data);
          throw new Error(`Failed to create ${config.service_type} order`);
        }
      }
      
      return createdOrders;
    } finally {
      isCreatingOrdersRef.current = false;
      setCreatingOrders(false);
    }
  };

  const handleStripePayment = async () => {
    if (submitted || creatingOrders) return; // Prevent double submission
    setSubmitted(true);
    setError('');
    
    try {
      const createdOrders = await createOrders();
      
      if (createdOrders.length === 0) {
        // Orders might already exist - try to fetch them
        console.log('No new orders created, checking for existing orders...');
        const existingOrdersRes = await axios.get(`${API}/orders?company_id=${companyId}&status=draft`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingOrders = existingOrdersRes.data.orders || [];
        
        if (existingOrders.length > 0) {
          // Use first existing order for payment
          const firstOrder = existingOrders[0];
          const paymentRes = await axios.post(`${API}/payments/stripe/checkout`, {
            order_id: firstOrder.id,
            origin_url: window.location.origin,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (paymentRes.data.checkout_url) {
            window.location.href = paymentRes.data.checkout_url;
            return;
          }
        }
        throw new Error('No orders available for payment');
      }
      
      // Use first order for Stripe session
      const firstOrder = createdOrders[0];
      
      const paymentRes = await axios.post(`${API}/payments/stripe/checkout`, {
        order_id: firstOrder.id,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (paymentRes.data.checkout_url) {
        window.location.href = paymentRes.data.checkout_url;
      } else {
        setError('Failed to initiate payment. Please try Bank Transfer.');
        setSubmitted(false); // Reset only on error
      }
    } catch (err) {
      setError(err.message || 'Failed to process order');
      setSubmitted(false); // Reset only on error
    }
  };

  const handleBankTransfer = async () => {
    if (submitted || creatingOrders) return; // Prevent double submission
    setSubmitted(true);
    setError('');
    
    try {
      const createdOrders = await createOrders();
      // Even if orders already existed, show success
      setOrderSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to create orders');
      setSubmitted(false); // Reset only on error
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  // Success screen
  if (orderSuccess) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto text-center py-12">
          <div className="w-20 h-20 bg-[#059669] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-gray-900" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Order Placed Successfully!</h1>
          <p className="text-gray-400 mb-2">
            Your formation package orders have been created successfully.
          </p>
          
          <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 text-left my-8">
            <h3 className="text-white font-semibold mb-4">Payment Instructions</h3>
            <p className="text-gray-400 text-sm mb-4">
              Our team will contact you with payment instructions within 24 hours.
            </p>
            <p className="text-gray-400 text-sm mb-4">
              You can also email us at{' '}
              <a 
                href="mailto:hi@starteaseai.com" 
                className="text-[#059669] hover:underline"
              >
                hi@starteaseai.com
              </a>
              {' '}with your Order ID for faster processing.
            </p>
            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between">
                <span className="text-gray-500">Total Amount:</span>
                <span className="text-[#059669] font-semibold">${calculateTotal()}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-black"
              style={{ backgroundColor: '#059669' }}
            >
              View My Orders
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Back Link */}
      <Link 
        to={`/dashboard/companies/${companyId}/get-started`}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Journey Selection
      </Link>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Formation Package</h1>
        <p className="text-gray-400 text-sm mt-1">
          {company?.company_name} • {company?.state_of_formation}
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3].map((s, idx) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                s <= step ? 'bg-[#059669] text-gray-900' : 'bg-gray-800 text-gray-400'
              }`}>
                {s < step ? <Check className="w-5 h-5" /> : s}
              </div>
              <span className={`mt-2 text-xs ${s <= step ? 'text-white' : 'text-gray-500'}`}>
                {s === 1 ? 'Entity Type' : s === 2 ? 'Review' : 'Payment'}
              </span>
            </div>
            {idx < 2 && (
              <div className={`w-16 md:w-24 h-0.5 mx-2 ${s < step ? 'bg-[#059669]' : 'bg-gray-800'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Step 1: Select Entity Type */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto">
          <h2 className="text-lg font-semibold text-white mb-6">Select Entity Type</h2>
          
          <div className="space-y-4 mb-8">
            {ENTITY_TYPES.map((entity) => (
              <button
                key={entity.id}
                onClick={() => setSelectedEntity(entity)}
                className={`w-full p-5 rounded-lg border-2 text-left transition-all ${
                  selectedEntity?.id === entity.id
                    ? 'border-[#059669] bg-[#059669]/10'
                    : 'border-gray-800 bg-[#111827] hover:border-gray-600'
                }`}
                data-testid={`entity-${entity.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedEntity?.id === entity.id ? 'bg-[#059669]' : 'bg-gray-800'
                    }`}>
                      <Building2 className={`w-5 h-5 ${
                        selectedEntity?.id === entity.id ? 'text-black' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{entity.name}</h3>
                      <p className="text-gray-400 text-sm">{entity.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#059669] font-semibold">${entity.baseFee}</p>
                    <p className="text-gray-500 text-xs">+ state fee</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              disabled={!selectedEntity}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#059669' }}
              data-testid="step1-next-btn"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Review Package */}
      {step === 2 && selectedEntity && (
        <div className="max-w-2xl mx-auto">
          <h2 className="text-lg font-semibold text-white mb-6">Review Your Formation Package</h2>
          
          <div className="bg-[#111827] border border-gray-800 rounded-lg overflow-hidden mb-6">
            {/* Formation */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#059669]" />
                  <div>
                    <p className="text-white font-medium">{selectedEntity.name} Formation</p>
                    <p className="text-gray-500 text-sm">Official state registration</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white">${selectedEntity.baseFee}</p>
                  {stateFee > 0 && <p className="text-gray-500 text-xs">State fee: ${stateFee}</p>}
                </div>
              </div>
            </div>
            
            {/* EIN */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#059669]" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{SERVICE_FEES.ein.name}</p>
                      <span className="px-2 py-0.5 bg-[#059669]/20 text-[#059669] text-xs rounded-full">Required</span>
                    </div>
                    <p className="text-gray-500 text-sm">Required by IRS to open bank accounts and file taxes</p>
                  </div>
                </div>
                <p className="text-white">${SERVICE_FEES.ein.fee}</p>
              </div>
            </div>
            
            {/* Registered Agent */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#059669]" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{SERVICE_FEES.registered_agent.name}</p>
                      <span className="px-2 py-0.5 bg-[#059669]/20 text-[#059669] text-xs rounded-full">Required</span>
                    </div>
                    <p className="text-gray-500 text-sm">Required by law in all 50 US states</p>
                  </div>
                </div>
                <p className="text-white">${SERVICE_FEES.registered_agent.fee}{SERVICE_FEES.registered_agent.recurring}</p>
              </div>
            </div>
            
            {/* Mail Forwarding (Toggle) */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIncludeMailForwarding(!includeMailForwarding)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      includeMailForwarding 
                        ? 'border-[#059669] bg-[#059669]' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                    data-testid="mail-forwarding-toggle"
                  >
                    {includeMailForwarding && <Check className="w-3 h-3 text-black" />}
                  </button>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${includeMailForwarding ? 'text-white' : 'text-gray-400'}`}>
                        {SERVICE_FEES.mail_forwarding.name}
                      </p>
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">Optional</span>
                    </div>
                    <p className="text-gray-500 text-sm">Professional US business address</p>
                  </div>
                </div>
                <p className={includeMailForwarding ? 'text-white' : 'text-gray-500'}>
                  ${SERVICE_FEES.mail_forwarding.fee}{SERVICE_FEES.mail_forwarding.recurring}
                </p>
              </div>
            </div>
            
            {/* Total */}
            <div className="p-4 bg-[#0b1310]">
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold">Total</p>
                <p className="text-[#059669] text-xl font-bold">${calculateTotal()}</p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors"
              style={{ backgroundColor: '#059669' }}
              data-testid="step2-next-btn"
            >
              Proceed to Payment
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Payment */}
      {step === 3 && (
        <div className="max-w-2xl mx-auto">
          <h2 className="text-lg font-semibold text-white mb-6">Choose Payment Method</h2>
          
          <div className="space-y-4 mb-8">
            {/* Card Payment */}
            <button
              onClick={() => setPaymentMethod('card')}
              className={`w-full p-5 rounded-lg border-2 text-left transition-all ${
                paymentMethod === 'card'
                  ? 'border-[#059669] bg-[#059669]/10'
                  : 'border-gray-800 bg-[#111827] hover:border-gray-600'
              }`}
              data-testid="payment-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#059669]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Pay with Card</h3>
                  <p className="text-gray-400 text-sm">Secure payment via Stripe</p>
                </div>
              </div>
            </button>
            
            {/* Bank Transfer */}
            <button
              onClick={() => setPaymentMethod('bank')}
              className={`w-full p-5 rounded-lg border-2 text-left transition-all ${
                paymentMethod === 'bank'
                  ? 'border-[#059669] bg-[#059669]/10'
                  : 'border-gray-800 bg-[#111827] hover:border-gray-600'
              }`}
              data-testid="payment-bank"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#059669]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Pay by Bank Transfer</h3>
                  <p className="text-gray-400 text-sm">Manual transfer — verified by our team</p>
                </div>
              </div>
            </button>
          </div>
          
          {/* Order Summary */}
          <div className="bg-[#111827] border border-gray-800 rounded-lg p-4 mb-8">
            <h3 className="text-white font-medium mb-3">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-400">
                <span>{selectedEntity?.name} Formation</span>
                <span>${selectedEntity?.baseFee}</span>
              </div>
              {stateFee > 0 && (
                <div className="flex justify-between text-gray-400">
                  <span>State Fee</span>
                  <span>${stateFee}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-400">
                <span>EIN (Tax ID)</span>
                <span>${SERVICE_FEES.ein.fee}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Registered Agent</span>
                <span>${SERVICE_FEES.registered_agent.fee}/yr</span>
              </div>
              {includeMailForwarding && (
                <div className="flex justify-between text-gray-400">
                  <span>Mail Forwarding</span>
                  <span>${SERVICE_FEES.mail_forwarding.fee}/yr</span>
                </div>
              )}
              <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between">
                <span className="text-white font-semibold">Total</span>
                <span className="text-[#059669] font-semibold">${calculateTotal()}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            
            {paymentMethod === 'card' ? (
              <button
                onClick={handleStripePayment}
                disabled={submitted || creatingOrders}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#059669' }}
                data-testid="pay-card-btn"
              >
                {submitted || creatingOrders ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {creatingOrders ? 'Creating Orders...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    Pay ${calculateTotal()}
                    <CreditCard className="w-5 h-5" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleBankTransfer}
                disabled={submitted || creatingOrders}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#059669' }}
                data-testid="pay-bank-btn"
              >
                {submitted || creatingOrders ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {creatingOrders ? 'Creating Orders...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    Place Orders
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default FormationPackagePage;
